import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const WaveformContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WaveformBars = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 100px;
`;

const WaveBar = styled(motion.div)`
  width: 4px;
  background: linear-gradient(
    to top,
    ${({ theme }) => theme.colors.primary[500]},
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: 2px;
  min-height: 4px;
  opacity: 0.9;
`;

const ProcessingText = styled(motion.div)`
  position: absolute;
  bottom: 20px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary[400]};
  font-weight: 500;
`;

const WaveformVisualizer = ({ audioStream, isProcessing, processingText }) => {
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const [bars, setBars] = React.useState(Array(30).fill(4));

  useEffect(() => {
    if (!audioStream || isProcessing) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(audioStream);
    
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    source.connect(analyser);
    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    const updateBars = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      
      const newBars = [];
      const barCount = 30;
      const step = Math.floor(dataArrayRef.current.length / barCount);
      
      for (let i = 0; i < barCount; i++) {
        const value = dataArrayRef.current[i * step];
        const height = Math.max(4, (value / 255) * 100);
        newBars.push(height);
      }
      
      setBars(newBars);
      animationRef.current = requestAnimationFrame(updateBars);
    };

    updateBars();

    return () => {
      source.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audioContext.close();
    };
  }, [audioStream, isProcessing]);

  if (isProcessing) {
    return (
      <WaveformContainer>
        <WaveformBars>
          {Array(30).fill(0).map((_, index) => (
            <WaveBar
              key={index}
              animate={{ 
                height: [20, 40, 20],
                opacity: [0.3, 0.9, 0.3]
              }}
              transition={{ 
                duration: 1.5,
                ease: "easeInOut",
                repeat: Infinity,
                delay: index * 0.05
              }}
            />
          ))}
        </WaveformBars>
        {processingText && (
          <ProcessingText
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {processingText}
          </ProcessingText>
        )}
      </WaveformContainer>
    );
  }

  return (
    <WaveformContainer>
      <WaveformBars>
        {bars.map((height, index) => (
          <WaveBar
            key={index}
            animate={{ height: `${height}px` }}
            transition={{ 
              duration: 0.1,
              ease: "linear"
            }}
            style={{
              opacity: 0.6 + (height / 100) * 0.4
            }}
          />
        ))}
      </WaveformBars>
    </WaveformContainer>
  );
};

export default WaveformVisualizer;
